/**
 * Action bar for segment dashboards — matches frontend's SegmentActionBar exactly.
 * Shows: "Segment Overview" title | pencil edit icon | "Actions" button
 */

import { useState, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, Menu, MenuItem } from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Close as CloseIcon } from '@mui/icons-material';

type Props = {
  editing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  title?: string;
  /** Extra menu items for the Actions dropdown */
  actions?: Array<{ label: string; onClick: () => void; danger?: boolean }>;
};

export const PageBuilderActionBar = ({
  editing,
  onEdit,
  onSave,
  onCancel,
  title = 'Segment Overview',
  actions,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleActionsClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  }, []);

  const handleActionsClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  return (
    <ActionBarRoot>
      <BarLeft>
        <BarTitle>{title}</BarTitle>
      </BarLeft>
      <BarRight>
        {editing ? (
          <>
            <IconBtn onClick={onCancel} title="Cancel">
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconBtn>
            <IconBtn onClick={onSave} title="Save">
              <SaveIcon sx={{ fontSize: 18 }} />
            </IconBtn>
          </>
        ) : (
          <IconBtn onClick={onEdit} title="Edit dashboard">
            <EditIcon sx={{ fontSize: 18 }} />
          </IconBtn>
        )}
        <ActionsBtn onClick={handleActionsClick}>
          Actions
        </ActionsBtn>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleActionsClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { minWidth: 160, mt: 0.5 } } }}
        >
          {!editing && (
            <MenuItem onClick={() => { handleActionsClose(); onEdit(); }}>Edit Dashboard</MenuItem>
          )}
          {actions?.map((action, i) => (
            <MenuItem
              key={i}
              onClick={() => { handleActionsClose(); action.onClick(); }}
              sx={action.danger ? { color: 'error.main' } : undefined}
            >
              {action.label}
            </MenuItem>
          ))}
        </Menu>
      </BarRight>
    </ActionBarRoot>
  );
};

// ─── Styled — matches frontend SegmentActionBar / GroupCardHeader ────────────

const ActionBarRoot = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 16px',
  borderBottom: '1px solid var(--sec-01, #e5e5e5)',
});

const BarLeft = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

const BarTitle = styled('span')({
  fontSize: '20px',
  fontFamily: 'var(--font-semibold, inherit)',
  fontWeight: 600,
  color: 'var(--black, #323232)',
});

const BarRight = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const IconBtn = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: '8px',
  color: 'var(--pri-02, #28AAE1)',
  transition: 'color 0.2s',
  '&:hover': {
    color: 'var(--pri-02, #1D94C7)',
  },
});

const ActionsBtn = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '100px',
  padding: '8px 20px',
  height: '40px',
  backgroundColor: 'var(--pri-02, #28AAE1)',
  border: 'none',
  borderRadius: '5px',
  color: '#ffffff',
  fontSize: '14px',
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: 'var(--pri-02-dark, #1D94C7)',
  },
});
